import { DatePickerCalendar, DatePickerInput, DatePickerPopover, DateRangePicker } from '@finn-no/troika-react-datepicker';
import { connect } from 'react-redux';
import { useConfig } from 'shared/config/ConfigContext';
import { safeFormatISODate, safeParseISO } from 'shared/util/datetime-fns';
import { isMobile } from 'shared/util/mobile';
import type { ApiQueryParams, FlexibilityOption, FlexibleDate, PaleState } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';
import { actions as filterActions } from '@/state/filterActions';
import { FlexibleDateHandler } from './FlexibleDateHandler';

import '@finn-no/troika-react-datepicker/dist/style.css';

interface Props {
    departureDate?: FlexibleDate;
    returnDate?: FlexibleDate;
    changeDepartureDate: (date: string | null) => void;
    changeReturnDate: (date: string | null) => void;
    changeFlexibleDeparture: (flex: number) => void;
    changeFlexibleReturn: (flex: number) => void;
    triggerFetch: (apiUrl: string, queryParam?: ApiQueryParams) => void;
    horizontal?: boolean;
}

export const addFlexibilityOptions = (
    flexibilityOptions: FlexibilityOption[],
    departureFlexibility?: number,
    returnFlexibility?: number,
): void => {
    if (departureFlexibility && !flexibilityOptions.some((option) => option.value === departureFlexibility)) {
        flexibilityOptions.push({ value: departureFlexibility, text: `±${departureFlexibility} dager` });
    }
    if (returnFlexibility && !flexibilityOptions.some((option) => option.value === returnFlexibility)) {
        flexibilityOptions.push({ value: returnFlexibility, text: `±${returnFlexibility} dager` });
    }
    flexibilityOptions.sort((a: FlexibilityOption, b: FlexibilityOption): number => a.value - b.value);
};

export const DatePicker = ({
    departureDate = {},
    returnDate = {},
    changeDepartureDate,
    changeReturnDate,
    changeFlexibleDeparture,
    changeFlexibleReturn,
    triggerFetch,
    horizontal = false,
}: Props) => {
    const { apiUrl } = useConfig();
    const changeDatesAndFetch = (newDates): void => {
        changeDepartureDate(safeFormatISODate(newDates.startDate));
        changeReturnDate(safeFormatISODate(newDates.endDate));
        triggerFetch(apiUrl);
    };
    const changeFlexibility = (flex: string): void => {
        changeFlexibleDeparture(~~flex);
        changeFlexibleReturn(~~flex);
        triggerFetch(apiUrl);
    };
    const numberOfMonths = isMobile() ? 1 : 2;

    const flexibilityOptions: FlexibilityOption[] = displayValues.flexibilityOptions;
    addFlexibilityOptions(flexibilityOptions, departureDate.flexibility, returnDate.flexibility);

    return (
        <div className="grid u-valign-top">
            <div className={`grid__unit ${horizontal ? ' u-r-size1of2 u-pb32' : ' u-pb32'}`}>
                <label htmlFor="date-range-start" className="u-screen-reader-only">
                    Avreisedato
                </label>
                <label htmlFor="date-range-end" className="u-screen-reader-only">
                    Hjemreisedato
                </label>
                <DateRangePicker
                    startDate={safeParseISO(departureDate.date)}
                    endDate={safeParseISO(returnDate.date)}
                    minimumNights={1}
                    onChange={(newDates) => changeDatesAndFetch(newDates)}>
                    <div className="input input--date u-mb0">
                        <div className="date-range-label-wrap">
                            <label htmlFor="date-range-start" className="u-screen-reader-only u-size1of2 u-pr4">
                                Avreisedato
                            </label>
                            <label htmlFor="date-range-end" className="u-screen-reader-only u-size1of2 u-pl4">
                                Hjemreisedato
                            </label>
                        </div>
                        <div className="u-position-relative calendar-flex">
                            <div className="u-display-inline-block u-size1of2 u-pr4">
                                <DatePickerInput
                                    dateField="startDate"
                                    as="input"
                                    className="start-date"
                                    id="date-range-start"
                                    placeholder="Utreise"
                                />
                            </div>
                            <div className="u-display-inline-block u-size1of2 u-pl4">
                                <DatePickerInput
                                    dateField="endDate"
                                    as="input"
                                    className="end-date"
                                    id="date-range-end"
                                    placeholder="Hjemreise"
                                />
                            </div>
                        </div>
                        <DatePickerPopover className="date-popover" attachTo="startDate">
                            <DatePickerCalendar numberOfMonths={numberOfMonths} />
                        </DatePickerPopover>
                    </div>
                </DateRangePicker>
            </div>
            <FlexibleDateHandler
                className={horizontal ? ' u-r-size1of2 u-pl8--gt768 u-mt-22--gt768' : 'u-size1of1'}
                value={departureDate.flexibility || 0}
                values={flexibilityOptions}
                onChange={changeFlexibility}
            />
        </div>
    );
};

const mapStateToProps = (state: PaleState): Pick<Props, 'departureDate' | 'returnDate'> => ({
    departureDate: state.filters.departureDate,
    returnDate: state.filters.returnDate,
});

const mapDispatchToProps = {
    changeDepartureDate: filterActions.changeDepartureDate,
    changeReturnDate: filterActions.changeReturnDate,
    resetDepartureDate: filterActions.changeDepartureDate,
    resetReturnDate: filterActions.changeReturnDate,
    changeFlexibleDeparture: filterActions.changeFlexibleDeparture,
    changeFlexibleReturn: filterActions.changeFlexibleReturn,
};

export default connect(mapStateToProps, mapDispatchToProps)(DatePicker);
